import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Label } from '../../components/ui/Form';
import { useToast } from '../../contexts/ToastContext';
import { authAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const passwordSchema = z.object({
  new_password: z.string().min(8, 'Password must be at least 8 characters'),
  confirm_password: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.new_password === data.confirm_password, {
  message: 'Passwords do not match',
  path: ['confirm_password'],
});

const ForcePasswordChange = () => {
  const { toast } = useToast();
  const { refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      new_password: '',
      confirm_password: '',
    },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await authAPI.changePassword({ new_password: data.new_password });
      await refreshUserData();
      toast.success('Password updated', 'Please use your new password next time.');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to change password', error.response?.data?.detail || 'Please try again');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-orange-500 via-orange-50 to-amber-400 flex items-center justify-center p-4 overflow-hidden">
      <div className="absolute inset-0 opacity-10 pointer-events-none z-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-orange-500 rounded-full filter blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-amber-400 rounded-full filter blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md relative z-20"
      >
        <Card className="shadow-strong border-0">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-center">
              Change Your Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div>
                <Label htmlFor="new_password">New Password</Label>
                <Controller
                  name="new_password"
                  control={control}
                  render={({ field }) => (
                    <div className="relative">
                      <Input
                        id="new_password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter a new password"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  )}
                />
                {errors.new_password && (
                  <div className="flex items-center mt-1 text-red-500 text-sm">
                    <AlertCircle size={14} className="mr-1" />
                    {errors.new_password.message}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="confirm_password">Confirm Password</Label>
                <Controller
                  name="confirm_password"
                  control={control}
                  render={({ field }) => (
                    <div className="relative">
                      <Input
                        id="confirm_password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Confirm your new password"
                        {...field}
                      />
                    </div>
                  )}
                />
                {errors.confirm_password && (
                  <div className="flex items-center mt-1 text-red-500 text-sm">
                    <AlertCircle size={14} className="mr-1" />
                    {errors.confirm_password.message}
                  </div>
                )}
              </div>

              <Button type="submit" className="w-full" loading={loading} size="lg">
                Update Password
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default ForcePasswordChange;
